import { Box, Card, CardContent, Typography } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { axiosRequestData } from '../../../helpers/axios';
import { AxiosResponse } from 'axios';
import QUESTIONS, { getNextQuestion, PlayerProfileField } from './surveyQuestions';
import SurveyQuestionMultiChoice from './surveyQuestionMultiChoice';
import SurveyQuestionHeader from './surveyQuestionHeader';
import SurveyQuestionFooter from './surveyQuestionFooter';
import { onboardingSurveyAnswered, onboardingSurveyCompleted, onboardingSurveyBackArrowUsed } from '../../mixPanelEvents/mixPanelEvents';

const containerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '90vw',
    maxWidth: 660,
    maxHeight: '85vh',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: 2,
    paddingBottom: 1,
    borderRadius: 1,
    display: 'flex',
    flexDirection: 'column',
};

type SurveyQuestionContainerProps = {
    questionId: number;
    setQuestionId: (id: number) => void;
    closeSurvey: () => void;
    answerHistory: { questionId: number; answer: string }[];
    setAnswerHistory: React.Dispatch<
        React.SetStateAction<{ questionId: number; answer: string }[]>
    >;
};

const SurveyQuestionContainer: React.FC<SurveyQuestionContainerProps> = ({
    questionId,
    setQuestionId,
    closeSurvey,
    answerHistory,
    setAnswerHistory,
}) => {
    const question = useMemo(() => QUESTIONS[questionId], [questionId]);
    const [selected, setSelected] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);
    const [wentBack, setWentBack] = useState(false);

    async function saveToPlayerProfile(field: PlayerProfileField, value: string): Promise<AxiosResponse<any, any>> {
         // the below is a workaround for the fact that 3 options in the scoring goal question point to "bogey-golfer"
        if (value.startsWith('bogey-golfer')) {
            value = 'bogey-golfer';
        }
        const data = {
            'player_profile': {
                [field]: value,
            }
        };
        return axiosRequestData('POST', '/wp-json/pg/v1/user/edit?fromSurvey=welcome', data);
    }

    const goBack = useCallback(() => {
        if (answerHistory.length === 0) return;

        const last = answerHistory[answerHistory.length - 1];
        const currentStepField = QUESTIONS[last.questionId].playerProfileField;

        onboardingSurveyBackArrowUsed(currentStepField, 'onboarding');

        setQuestionId(last.questionId);
        setSelected(last.answer);
        setAnswerHistory((prev) => prev.slice(0, -1));
        setWentBack(true);
    }, [answerHistory, setQuestionId, setSelected, setAnswerHistory]);

    const saveAndNext = useCallback(async () => {
        setSaving(true);
        if (selected != null) {
            saveToPlayerProfile(question.playerProfileField, selected)
                .then(res => {
                    onboardingSurveyAnswered(question.playerProfileField, selected, 'onboarding');
                    setSaving(false);
                    const next = getNextQuestion(questionId, selected);
                    if (next === -1) {
                        onboardingSurveyCompleted('onboarding');
                        closeSurvey();
                    } else {
                        setAnswerHistory((prev) => [
                            ...prev,
                            { questionId, answer: selected }
                        ]);
                        setQuestionId(next);
                        setSelected(null);
                        setWentBack(false);
                    }
                })
                .catch(reason => {
                    setSaving(false);
                    console.log('Unexpected error when saving player profile: ', reason);
                });
        }
    }, [setSaving, selected, setSelected, questionId, setAnswerHistory]);

    const handleSelect = (value: string) => {
        setSelected(value);
        setWentBack(false);
    };

    return (
        <Card sx={containerStyle}>
            <SurveyQuestionHeader closeSurvey={closeSurvey} currentQuestionId={questionId} goBack={goBack} />
            <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
            }}>
                <Typography
                    variant="h5"
                    id='modal-title'
                    sx={{ fontWeight: 'bold', textTransform: "capitalize" }}
                    align="center"
                >
                    {question.questionText}
                </Typography>
                <SurveyQuestionMultiChoice
                    options={question.options}
                    selected={selected}
                    setSelected={handleSelect}
                    showPreviouslySelected={wentBack}
                />
            </CardContent>
            <SurveyQuestionFooter
                onSave={saveAndNext}
                isFetching={saving}
                optionSelected={selected != null}
            />
        </Card>
    );
};

export default SurveyQuestionContainer;